import { ThemeToggle } from '../components/theme/ThemeToggle';
import { LegalBackLink } from '../components/legal/LegalBackLink';
import { Helmet } from 'react-helmet-async';

function Privacy() {
  return (
    <div className="min-h-screen bg-base-100">
      {/* Legal pages: noindex so AI assistants don't cite our specific
          legal language (it changes; AI snapshots get stale and misleading).
          Robots still crawl other links from here. */}
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="mb-8">
          <div className="mb-6 flex items-center justify-between gap-4">
            <LegalBackLink />
            {/* Sun/moon (dark mode D9, msg 7524): absent until dark is available (D5) */}
            <ThemeToggle testId="theme-toggle-header" />
          </div>
          <h1 className="text-4xl font-bold text-base-content mb-2">Privacy Policy</h1>
          <p className="text-base-content/60">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-(--ee-surface) border border-(--ee-tw-gray-200-line) rounded-2xl p-8 prose prose-sm max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">1. Introduction</h2>
            <p className="text-base-content/80 leading-relaxed">
              At EmpathEase, we respect your privacy and are committed to protecting your personal
              data. This privacy policy explains how we collect, use, and safeguard your information
              when you use our service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              2. Information We Collect
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              We collect the following types of information:
            </p>
            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              2.1 Account Information
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>Name and email address</li>
              <li>Password (encrypted)</li>
              <li>Account preferences and settings</li>
            </ul>
            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">2.2 Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>Course progress and quiz results</li>
              <li>Feature usage patterns</li>
              <li>Login activity and device information</li>
            </ul>
            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              2.3 AI Training Data (Optional)
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>Text inputs to Giraffe Translator</li>
              <li>Interactions with Needs Guesser</li>
              <li>AI feature usage patterns</li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3 italic">
              Note: AI training data is only collected if you explicitly consent. You can manage
              this preference in your profile settings at any time.
            </p>
            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              2.4 Voice Input to AI Features (Optional)
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                Audio you record when you use a voice-enabled AI feature (the Empy AI tutor, spoken
                quiz answers, Echo Arena roleplay, and the AI empathy buddy)
              </li>
              <li>The text transcript we generate from that audio so the feature can respond</li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3 italic">
              Note: Voice input is used only when you choose to speak instead of type. We transcribe
              it solely to operate the feature you are using and delete the audio recording
              immediately afterward. We do not record or store your live empathy conversations with
              other people. See Section 4 for details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              We use your information for the following purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>Providing and improving our educational services</li>
              <li>Managing your account and subscriptions</li>
              <li>Personalizing your learning experience</li>
              <li>Communicating with you about service updates</li>
              <li>Improving AI features (only with your explicit consent)</li>
              <li>Analyzing usage patterns to enhance user experience</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              4. Voice Input &amp; Transcription
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              Several EmpathEase features let you speak instead of type. This section explains what
              happens to that audio.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.1 Which Features Use Your Voice
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              Voice input is recorded only when you choose a spoken option in:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>Empy, the AI tutor (voice messages)</li>
              <li>Spoken quiz answers</li>
              <li>Echo Arena roleplay practice</li>
              <li>The AI empathy buddy, when you practice an exchange with our AI</li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3">
              If you type instead of speaking, no audio is recorded.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">4.2 How It Works</h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                <strong>Recording:</strong> When you record a voice input, the audio is sent to our
                server over an encrypted connection.
              </li>
              <li>
                <strong>Transcription:</strong> Speech-to-text is performed using OpenAI&apos;s
                Whisper service so the feature can understand and respond to you.
              </li>
              <li>
                <strong>Audio deletion:</strong> Your audio recording is deleted immediately after
                it is transcribed. We do not keep the audio file.
              </li>
              <li>
                <strong>Transcript:</strong> The resulting text is used to operate the feature (for
                example, to grade a spoken quiz answer or generate a coaching reply) and is kept as
                part of your activity in that feature.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.3 Third-Party Processing
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              Speech-to-text is performed by OpenAI using its Whisper service. OpenAI processes the
              audio solely to generate the transcript and, under its API data-use terms, does not
              use it to train its models.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.4 Live Empathy Conversations Are Not Recorded
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              When you talk with another person in a live video empathy session or a circle, that
              audio, and any video, is carried in real time, peer-to-peer, over our self-hosted
              video and audio infrastructure. We do not use a third-party video provider, and we do
              not record, transcribe, or store these conversations.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.5 What We Do NOT Do
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>We do not record your live empathy conversations with other people</li>
              <li>We do not keep your voice recordings after they are transcribed</li>
              <li>
                We do not use your voice input to train AI models (OpenAI does not train on it under
                its API terms, and neither do we)
              </li>
              <li>
                We do not sell your voice input or transcripts, or share them with anyone other than
                the transcription provider named above
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              5. Data Sharing and Disclosure
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              We do not sell your personal data. We may share your information only in the following
              circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>With your explicit consent</li>
              <li>
                With service providers who assist in operating our platform (under strict
                confidentiality agreements)
              </li>
              <li>When required by law or to protect our legal rights</li>
              <li>
                In connection with a business transfer or acquisition (with advance notice to users)
              </li>
            </ul>
            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              5.1 Service Providers
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              We use the following third-party service providers to operate EmpathEase:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                <strong>Stripe</strong> - Payment processing for subscriptions. Handles payment card
                information; we do not store your full card details.
              </li>
              <li>
                <strong>OpenAI</strong> - AI features (AI Tutor, Giraffe Translator, Needs Guesser,
                Echo Arena) and speech-to-text transcription of voice input to our AI features.
                Processes only the inputs needed for these features; under OpenAI&apos;s API terms
                your data is not used to train its models.
              </li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3">
              All service providers are contractually obligated to protect your data and use it only
              for the purposes we specify.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">6. Data Security</h2>
            <p className="text-base-content/80 leading-relaxed">
              We implement industry-standard security measures to protect your data, including
              encryption, secure authentication, and regular security audits. However, no method of
              transmission over the internet is 100% secure, and we cannot guarantee absolute
              security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">7. Your Data Rights</h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                <strong>Access:</strong> Request a copy of your personal data
              </li>
              <li>
                <strong>Correction:</strong> Update or correct inaccurate information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your account and data
              </li>
              <li>
                <strong>Consent Management:</strong> Change your AI training data consent at any
                time
              </li>
              <li>
                <strong>Data Portability:</strong> Request your data in a machine-readable format
              </li>
              <li>
                <strong>Objection:</strong> Object to certain types of data processing
              </li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3">
              To exercise these rights, please contact us at privacy@empathease.app or manage your
              preferences in your profile settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              8. Cookies and Tracking
            </h2>
            <p className="text-base-content/80 leading-relaxed">
              We use essential cookies to maintain your session and provide core functionality. We
              do not use third-party advertising cookies. You can manage cookie preferences through
              your browser settings.
            </p>
            <p className="text-base-content/80 leading-relaxed mt-3">
              Our public games and stories at games.empathease.app use Google Analytics to measure
              anonymous usage (no account, no personal information). If you have turned analytics
              off in the app, links from the app to those pages carry your opt-out with them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-base-content/80 leading-relaxed">
              Our service is not intended for children under 13 years of age. We do not knowingly
              collect personal information from children. If you believe we have collected
              information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              10. International Data Transfers
            </h2>
            <p className="text-base-content/80 leading-relaxed">
              Your data may be stored and processed in different countries. We ensure appropriate
              safeguards are in place to protect your data in accordance with this privacy policy
              and applicable laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">11. Data Retention</h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              We retain your personal data for as long as your account is active or as needed to
              provide our services. If you delete your account, we will delete your personal data
              within 30 days, except where we are required to retain it for legal or compliance
              purposes.
            </p>
            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              11.1 Voice Input &amp; Transcripts
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                Audio you record for a voice-enabled AI feature is deleted immediately after it is
                transcribed. We do not store voice recordings.
              </li>
              <li>
                The text transcript is kept as part of your activity in that feature (for example,
                your quiz history or tutor conversation) and is deleted when you delete your
                account.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              12. Changes to This Policy
            </h2>
            <p className="text-base-content/80 leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any
              material changes by email or through a prominent notice on our service. Your continued
              use after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">13. Contact Us</h2>
            <p className="text-base-content/80 leading-relaxed">
              If you have questions or concerns about this privacy policy or our data practices,
              please contact us at:
            </p>
            <ul className="list-none pl-0 mt-3 space-y-1 text-base-content/80">
              <li>Email: privacy@empathease.app</li>
              <li>Support: support@empathease.app</li>
            </ul>
          </section>

          <div className="mt-12 pt-8 border-t border-(--ee-tw-gray-200-line)">
            <p className="text-sm text-(--ee-public-ink-50)">Effective Date: December 2024</p>
            <p className="text-sm text-(--ee-public-ink-50) mt-2">
              This policy complies with GDPR (EU), CCPA (California), and applicable U.S. state
              privacy laws. For questions about our privacy practices, contact us at
              privacy@empathease.app.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
